/**
 * API Token Service
 *
 * Manages user-scoped API tokens for external integrations (LLM, automation).
 * Tokens authenticate via x-api-token header instead of session.
 */

// API URL from environment
const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";

type ClerkLike = {
  session?: { getToken: () => Promise<string | null> } | null;
};

type ApiTokenErrorPayload = {
  code?: string;
  error?: string;
};

// Clerk instance (set by App.tsx)
let clerkInstance: ClerkLike | null = null;

export function setClerkInstanceForTokens(clerk: ClerkLike) {
  clerkInstance = clerk;
}

// ── Types ──

export interface ApiToken {
  id: string;
  name: string;
  scopes: string[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

// ── Error Handling ──

class ApiTokenError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiTokenError";
  }
}

async function fetchWithAuth<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  if (!clerkInstance) {
    throw new ApiTokenError("NOT_INITIALIZED", "Clerk not initialized");
  }

  const token = await clerkInstance.session?.getToken();
  if (!token) {
    throw new ApiTokenError("NOT_AUTHENTICATED", "User not authenticated");
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const data: ApiTokenErrorPayload = await response.json().catch(() => ({}));
    throw new ApiTokenError(
      data.code || "API_ERROR",
      data.error || "Request failed",
    );
  }

  return response.json();
}

// ── API Functions ──

/**
 * List all API tokens for the current user
 */
export async function listApiTokens(): Promise<ApiToken[]> {
  const data = await fetchWithAuth<{ tokens: ApiToken[] }>("/api/users/tokens");
  return data.tokens;
}

/**
 * Create a new API token
 * Returns the raw token value (only shown once)
 */
export async function createApiToken(
  name: string,
  options?: {
    scopes?: string[];
    expiresAt?: string | null;
  },
): Promise<{ token: string; tokenInfo: ApiToken }> {
  const data = await fetchWithAuth<{ token: string; tokenInfo: ApiToken }>(
    "/api/users/tokens",
    {
      method: "POST",
      body: JSON.stringify({
        name,
        scopes: options?.scopes || ["library:write"],
        expiresAt: options?.expiresAt || null,
      }),
    },
  );

  return data;
}

/**
 * Delete an API token
 */
export async function deleteApiToken(tokenId: string): Promise<void> {
  await fetchWithAuth(`/api/users/tokens/${tokenId}`, { method: "DELETE" });
}
